import React from 'react';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import CoachIdentityDecisionPanel from './CoachIdentityDecisionPanel';
import {
  CLASS_AUDIT_STATE,
  CLASS_AUDIT_STATE_LABEL,
  COACH_ATTRIBUTION_STATE,
  EVALUATION_REVIEW_STATE,
  EVALUATION_REVIEW_STATE_LABEL,
  classAuditProgress,
  coachAttributionProgress,
  evaluationReviewProgress,
} from '../utils/coachAuditProgress';

// Identity rule -> chip treatment. The chip describes the identity-wide RULE only, never whether the
// identity's evaluations still need work -- that is the Coach attribution column. So "No rule" is a
// neutral fact rather than an alarm (an evaluation-complete identity legitimately has none), and the
// one loud state is a rule whose premise broke. "Pending build" is a caption, not a chip, because it
// is a timing fact about the warehouse and not a finding about the identity.
export const REVIEW_STATE_CHIP = {
  needs_review_new: { label: 'No rule', color: 'default', variant: 'filled' },
  needs_review_changed: { label: 'Rule changed — review', color: 'error', variant: 'filled' },
  accepted: { label: 'Rule accepted', color: 'default', variant: 'outlined' },
  accepted_pending_build: { label: 'Rule accepted', color: 'default', variant: 'outlined' },
};

const CLASS_CHIP_COLOR = {
  [CLASS_AUDIT_STATE.NOT_STARTED]: 'default',
  [CLASS_AUDIT_STATE.IN_PROGRESS]: 'info',
  [CLASS_AUDIT_STATE.COMPLETE]: 'success',
};

const CATEGORY_LABEL = {
  dash_ambiguous_review: 'Ambiguous',
  dash_name_differs_review: 'Name differs',
  typed_only_dash_not_configured: 'Not configured in Dash',
  typed_only_no_dash_roster: 'No Dash roster',
  typed_name_missing: 'No name submitted',
};

// The accepted coach as the auditor should read it: the live Dash name where the employee still
// exists, otherwise the name captured when the decision was accepted. Never the typed Form name --
// showing that would misrepresent an accepted link as an unreviewed one.
function acceptedCoachText(row) {
  if (!row.decision) return '—';
  if (row.decision === 'keep_provisional') return 'Form coach (audited)';
  return row.employee_display_name || row.snapshot_coach_display_name || row.canonical_employee_id || '—';
}

// The class outcomes other than a confirmed session, as a caption -- so "2 of 3" never hides that the
// third was deliberately judged unresolved, or that another left the count as "no Dash class expected".
function classOutcomeCaption(row) {
  const parts = [];
  const unresolved = Number(row.class_unresolved_count) || 0;
  const none = Number(row.class_no_occurrence_count) || 0;
  if (unresolved) parts.push(`${unresolved} unresolved`);
  if (none) parts.push(`${none} no Dash class expected`);
  return parts.join(' · ');
}

// Class audit: "3 of 3 · Complete". No class-applicable evaluation (no Dash franchise, e.g. Sofive, or
// every evaluation judged "no Dash class expected") reads "n/a" -- never "0 of 0 · Complete", which
// would claim work that cannot exist.
function ClassAuditCell({ row }) {
  const p = classAuditProgress(row);
  const caption = classOutcomeCaption(row);
  const captionEl = caption
    ? <Typography variant="caption" color="text.secondary" component="div">{caption}</Typography>
    : null;
  if (p.state === CLASS_AUDIT_STATE.NOT_APPLICABLE) {
    return (
      <>
        <span className="text-secondary" title="No Dash class to match: no Dash franchise (for example Sofive), or judged “no Dash class expected”.">
          n/a
        </span>
        {captionEl}
      </>
    );
  }
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
        <span>{p.done} of {p.total}</span>
        <Chip size="small" variant="outlined" sx={{ ml: 1 }}
          label={CLASS_AUDIT_STATE_LABEL[p.state]} color={CLASS_CHIP_COLOR[p.state] || 'default'} />
      </Box>
      {captionEl}
    </>
  );
}

const REVIEW_CHIP_COLOR = {
  [EVALUATION_REVIEW_STATE.NOT_STARTED]: 'default',
  [EVALUATION_REVIEW_STATE.IN_PROGRESS]: 'info',
  [EVALUATION_REVIEW_STATE.COMPLETE]: 'success',
};

// Evaluation review: "1 of 1 reviewed · Complete". Reviewed means an auditor recorded BOTH judgments,
// whatever they were -- it never means "matched to Dash".
function EvaluationReviewCell({ row }) {
  const p = evaluationReviewProgress(row);
  if (p.state === EVALUATION_REVIEW_STATE.NO_EVALUATIONS) return <span className="text-secondary">—</span>;
  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
        <span>{p.reviewed} of {p.total} reviewed</span>
        <Chip size="small" variant="outlined" sx={{ ml: 1 }}
          label={EVALUATION_REVIEW_STATE_LABEL[p.state]} color={REVIEW_CHIP_COLOR[p.state] || 'default'} />
      </Box>
      {p.partly > 0 && (
        <Typography variant="caption" color="text.secondary" component="div">
          {p.partly} with class only — coach not decided
        </Typography>
      )}
    </>
  );
}

// Coach attribution: "0 of 3 resolved", read from the warehouse's last build. While the server says
// reporting does not read evaluation reviews (integrationPending), saved coach choices are a
// separate note -- recorded work, not attribution. Since slice 2b the server says it does, the note
// is hidden, and saved reviews show up in the resolved count after the next warehouse build.
function CoachAttributionCell({ row, integrationPending }) {
  const p = coachAttributionProgress(row);
  if (p.state === COACH_ATTRIBUTION_STATE.NO_EVALUATIONS) return <span className="text-secondary">—</span>;
  // Every evaluation-level coach decision -- a Dash employee OR the Form coach kept.
  const decisions = p.selections + (Number(row.coach_form_count) || 0);
  return (
    <>
      <div style={{ whiteSpace: 'nowrap' }}>{p.resolved} of {p.total} resolved</div>
      {integrationPending && decisions > 0 && (
        <Typography variant="caption" color="text.secondary" component="div">
          {decisions} coach decision{decisions === 1 ? '' : 's'} recorded — not yet used in reporting
        </Typography>
      )}
    </>
  );
}

// One Form coach identity. Columns: (expand) | Franchise | Form name | Evals | Range | Candidates |
// Accepted coach | Identity rule | Evaluation review | Class audit | Coach attribution.
export default function CoachIdentityRow({
  row, candidates, onRowUpdate, isExpanded, onToggle, onCollapse, onEvaluationSaved, integrationPending = true,
}) {
  const chip = REVIEW_STATE_CHIP[row.review_state] || REVIEW_STATE_CHIP.needs_review_new;
  const detailId = `coach-identity-detail-${row.reporting_coach_key}`;
  const range = row.first_evaluation
    ? `${String(row.first_evaluation).slice(0, 10)} → ${String(row.last_evaluation).slice(0, 10)}`
    : '—';
  const category = CATEGORY_LABEL[row.review_category] || row.review_category || '—';
  // Always both halves, never a bare number. A lone "4" was read as four eligible coaches when it
  // meant four candidates of which none was eligible -- the two readings point at opposite actions.
  const candidateText = `${row.eligible_candidate_count || 0} eligible / ${row.candidate_count || 0} total`;

  return (
    <>
      <tr>
        <td style={{ width: 40 }}>
          <button
            type="button"
            className="btn btn-sm btn-link p-0"
            aria-expanded={isExpanded}
            aria-controls={detailId}
            aria-label={isExpanded ? 'Collapse identity details' : 'Expand identity details'}
            onClick={onToggle}
          >
            {isExpanded ? '▾' : '▸'}
          </button>
        </td>
        <td className="text-start text-secondary" title={row.com_code}>{row.com_code}</td>
        <td className="text-start" title={`${row.form_raw_name} · ${category}`}>
          {row.form_raw_name}
          {row.is_priority_audit === 1 && (
            // The first-priority set: a provisional key whose typed name exactly matches a validated
            // coach in the same franchise. Never auto-merged — merging would assert a link no person
            // has reviewed.
            <Chip size="small" label="priority" color="warning" variant="outlined" sx={{ ml: 1 }} />
          )}
        </td>
        <td className="text-start text-secondary">{row.evaluation_count}</td>
        <td className="text-start text-secondary" style={{ whiteSpace: 'nowrap' }}>{range}</td>
        <td className="text-start text-secondary" style={{ whiteSpace: 'nowrap' }}>{candidateText}</td>
        <td className="text-start text-secondary">{acceptedCoachText(row)}</td>
        <td className="text-start">
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Chip size="small" label={chip.label} color={chip.color} variant={chip.variant} />
            {row.pending_rebuild === true && (
              <Typography
                component="span"
                variant="caption"
                color="text.disabled"
                sx={{ ml: 1 }}
                title="Saved. CP-06 and CP-08 reflect this on the next warehouse build."
              >
                pending build
              </Typography>
            )}
          </Box>
        </td>
        <td className="text-start"><EvaluationReviewCell row={row} /></td>
        <td className="text-start"><ClassAuditCell row={row} /></td>
        <td className="text-start"><CoachAttributionCell row={row} integrationPending={integrationPending} /></td>
      </tr>

      {isExpanded && (
        <tr id={detailId}>
          {/* text-start opts out of the global `td { text-align: center }` in index.css, exactly as
              every sibling cell above already does. */}
          <td colSpan={11} className="text-start">
            <Box sx={{ px: 2, py: 1.5 }}>
              <CoachIdentityDecisionPanel row={row} candidates={candidates} onRowUpdate={onRowUpdate}
                onIdentityComplete={onCollapse} onEvaluationSaved={onEvaluationSaved} />
            </Box>
          </td>
        </tr>
      )}
    </>
  );
}
